import axios from 'axios';
import { appConstants } from '../../_constants/app.constants';

export const API = appConstants.API_URL;

export const MAX_SHIELD_BYTES = 1.5 * 1024 * 1024;

export function apiErr(err) {
  const d = err.response?.data;
  if (typeof d?.message === 'string') return d.message;
  if (typeof d?.Message === 'string') return d.Message;
  return err.message || 'Error desconocido';
}

const ORDINAL_NAMES = ['', '1º', '2º', '3º', '4º', '5º', '6º', '7º', '8º'];

function ordinal(n) {
  const i = Number(n);
  if (!Number.isFinite(i) || i < 1) return `${n}º`;
  return ORDINAL_NAMES[i] || `${i}º`;
}

export function formatGroupPositionLabel(p) {
  if (!p) return '';
  const g = p.group?.name || p.Group?.name || 'Grupo';
  // La etiqueta se compone a partir de la posición numérica para evitar depender
  // del campo `name` persistido (que podía llegar mal codificado, p. ej. "1� GRUPO A").
  const pos = Number(p.position ?? p.Position);
  if (Number.isFinite(pos) && pos >= 1) {
    return `${g} — ${ordinal(pos)}`;
  }
  const slot = (p.name || p.Name || '').trim() || 'Plaza';
  return `${g} — ${slot}`;
}

/** Etiqueta para una regla cross-group: ej. "Mejor 2º entre grupos", "2º mejor 3º entre grupos". */
export function formatCrossGroupRankLabel(position, index) {
  const p = Number(position);
  const i = Number(index);
  if (!Number.isFinite(p) || !Number.isFinite(i) || p < 1 || i < 1) return '';
  if (i === 1) return `Mejor ${ordinal(p)} entre grupos`;
  return `${ordinal(i)} mejor ${ordinal(p)} entre grupos`;
}

/**
 * Convención de fechas: las horas de partidos son **wall-clock locales del torneo**
 * (no UTC). El frontend envía y recibe strings tipo "YYYY-MM-DDTHH:mm[:ss]" sin
 * sufijo Z ni offset. NO usar `.toISOString()` para `dayHour`: convierte a UTC y
 * en zonas con offset distinto de 0 desplaza la hora (p. ej. 10:30 → 08:30 en CEST).
 */

/** Cuerpo PUT /api/tournamentmatches/{id}/finalmatch */
export function bracketMatchToPutBody(match) {
  return {
    tournamentId: match.tournamentId ?? match.TournamentId,
    name: match.name,
    dayHour: match.dayHour,
    nextMatchId: match.nextMatchId ?? null,
    localTeamId: match.localTeamId ?? null,
    foreignTeamId: match.foreignTeamId ?? null,
    winnerTeamId: match.winnerTeamId ?? null,
    groupPositionLocalId: match.groupPositionLocalId ?? null,
    groupPositionForeignId: match.groupPositionForeignId ?? null,
    localGoals: match.localGoals ?? null,
    foreignGoals: match.foreignGoals ?? null,
    fieldName: match.fieldName ?? null,
    isActive: Boolean(match.isActive),
    completed: Boolean(match.completed),
    order: match.order ?? null,
    crossGroupRankPositionLocal: match.crossGroupRankPositionLocal ?? null,
    crossGroupRankIndexLocal: match.crossGroupRankIndexLocal ?? null,
    crossGroupRankPositionForeign: match.crossGroupRankPositionForeign ?? null,
    crossGroupRankIndexForeign: match.crossGroupRankIndexForeign ?? null,
  };
}

export function toDateTimeLocalValue(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function roundRobinPairs(teamIds, doubleRound) {
  const ids = [...teamIds];
  const pairs = [];
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      pairs.push([ids[i], ids[j]]);
      if (doubleRound) pairs.push([ids[j], ids[i]]);
    }
  }
  return pairs;
}

/** Suma horas y devuelve formato wall-clock local ("YYYY-MM-DDTHH:mm"), no UTC. */
export function addHours(isoOrDate, hours) {
  const d = new Date(isoOrDate);
  d.setTime(d.getTime() + hours * 3600000);
  return toDateTimeLocalValue(d);
}

export async function postBracketMatch(body) {
  const { data } = await axios.post(`${API}/api/tournamentmatches/bracketmatch`, body);
  return data;
}

/** Eliminatoria vacía enlazada por nextMatchId (4, 8 o 16 clasificados). */
export async function seedKnockoutBracket(tournamentId, size, firstRoundStartIso, hoursBetweenRounds) {
  const h = (step) => addHours(firstRoundStartIso, step * hoursBetweenRounds);
  const base = {
    tournamentId,
    localTeamId: null,
    foreignTeamId: null,
    groupPositionLocalId: null,
    groupPositionForeignId: null,
    fieldName: null,
    order: null,
  };

  if (size === 4) {
    const final = await postBracketMatch({ ...base, name: 'Final', dayHour: h(2), nextMatchId: null, order: null });
    await postBracketMatch({ ...base, name: 'Semifinal 1', dayHour: h(0), nextMatchId: final.id, order: 1 });
    await postBracketMatch({ ...base, name: 'Semifinal 2', dayHour: h(0), nextMatchId: final.id, order: 2 });
    return 3;
  }

  if (size === 8) {
    const final = await postBracketMatch({ ...base, name: 'Final', dayHour: h(3), nextMatchId: null, order: null });
    const sf1 = await postBracketMatch({ ...base, name: 'Semifinal 1', dayHour: h(2), nextMatchId: final.id, order: 1 });
    const sf2 = await postBracketMatch({ ...base, name: 'Semifinal 2', dayHour: h(2), nextMatchId: final.id, order: 2 });
    await postBracketMatch({ ...base, name: 'Cuartos 1', dayHour: h(1), nextMatchId: sf1.id, order: 1 });
    await postBracketMatch({ ...base, name: 'Cuartos 2', dayHour: h(1), nextMatchId: sf1.id, order: 2 });
    await postBracketMatch({ ...base, name: 'Cuartos 3', dayHour: h(1), nextMatchId: sf2.id, order: 1 });
    await postBracketMatch({ ...base, name: 'Cuartos 4', dayHour: h(1), nextMatchId: sf2.id, order: 2 });
    return 7;
  }

  if (size === 16) {
    const final = await postBracketMatch({ ...base, name: 'Final', dayHour: h(4), nextMatchId: null, order: null });
    const sf1 = await postBracketMatch({ ...base, name: 'Semifinal 1', dayHour: h(3), nextMatchId: final.id, order: 1 });
    const sf2 = await postBracketMatch({ ...base, name: 'Semifinal 2', dayHour: h(3), nextMatchId: final.id, order: 2 });
    const qf1 = await postBracketMatch({ ...base, name: 'Cuartos 1', dayHour: h(2), nextMatchId: sf1.id, order: 1 });
    const qf2 = await postBracketMatch({ ...base, name: 'Cuartos 2', dayHour: h(2), nextMatchId: sf1.id, order: 2 });
    const qf3 = await postBracketMatch({ ...base, name: 'Cuartos 3', dayHour: h(2), nextMatchId: sf2.id, order: 1 });
    const qf4 = await postBracketMatch({ ...base, name: 'Cuartos 4', dayHour: h(2), nextMatchId: sf2.id, order: 2 });
    const qfs = [qf1, qf1, qf2, qf2, qf3, qf3, qf4, qf4];
    for (let i = 0; i < 8; i++) {
      await postBracketMatch({
        ...base,
        name: `Octavos ${i + 1}`,
        dayHour: h(1),
        nextMatchId: qfs[i].id,
        order: (i % 2) + 1,
      });
    }
    return 15;
  }

  throw new Error('Tamaño no soportado');
}
